import { useCallback, useEffect, useRef, useState } from 'react'
import SumsubWebSdk from '@sumsub/websdk-react'
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react'
import { Button } from '@/components/atoms'
import {
  KybForbiddenError,
  KybLevelUnresolvedError,
  kybService,
} from '@/services/kybService'
import type { KybLinkResponse, KybStatus } from '@/types/kyb'
import { cn } from '@/utils/cn'
import { BODY_TEXT, ERROR_TEXT, HELP_TEXT } from './styles'
import { SectionHeading } from './FormField'

interface KybStepProps {
  /**
   * Resolve (creating if needed) the organization id, flushing any pending
   * autosave first. Provided by the wizard — the `handleClaimDomain` pattern:
   * the applicant is created against the organization, so it must exist first,
   * and a just-typed `legal_form` must land before the backend resolves the
   * verification level from it.
   */
  resolveOrgId: () => Promise<string>
  /**
   * The token endpoint succeeded — the applicant exists server-side. The wizard
   * uses this to enable Continue; it is sticky, because "the applicant exists"
   * is a fact that does not revert.
   */
  onTokenIssued: (status: KybStatus) => void
  /**
   * The backend answered 409 `kyb_level_unresolved`: no usable `legal_form`.
   * The wizard jumps back to the profile step and highlights the field.
   */
  onEditProfile: () => void
  disabled?: boolean
}

type StepPhase = 'starting' | 'ready' | 'blocked' | 'forbidden' | 'error'

/**
 * The wizard's verification step — the embedded Sumsub WebSDK.
 *
 * ## Token flow
 * Mount → `resolveOrgId()` → `POST .../kyb/access-token` → render the SDK with
 * the token. The SDK is rendered ONLY after a token exists, so the legacy class
 * component mounts with a real token. The frontend never sends a level name —
 * the backend resolves it from the organization's `legal_form`.
 *
 * ## StrictMode
 * The SDK is a legacy class component and StrictMode double-mounts in dev. Two
 * defenses, both deliberate: `startRef` holds the in-flight token request so
 * the second mount awaits the first instead of POSTing again (the
 * `createOrgRef` pattern), and the backend is idempotent anyway — a unique
 * index plus duplicate-409 reuse mean at most one applicant per org, whatever
 * the client does. A double SDK mount with the SAME token is a serial re-init
 * of one applicant session, never a second applicant.
 *
 * ## Expiry
 * `expirationHandler` re-mints through `kybService.getAccessToken`, which sets
 * `skipAuthLogout` — a 401 there must never feed the global logout counter and
 * hard-redirect to /login while the iframe is open. There is no token-expiry
 * event: an expired token also surfaces as `idCheck.onError` with code
 * `invalid-token`, which is handled identically (tear down, offer retry).
 */
export const KybStep = ({
  resolveOrgId,
  onTokenIssued,
  onEditProfile,
  disabled,
}: KybStepProps) => {
  const [phase, setPhase] = useState<StepPhase>('starting')
  const [token, setToken] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [sdkNotice, setSdkNotice] = useState<string | null>(null)
  const [sdkSubmitted, setSdkSubmitted] = useState(false)
  const [link, setLink] = useState<KybLinkResponse | null>(null)
  const [mintingLink, setMintingLink] = useState(false)

  // --- refs that must survive a StrictMode remount -------------------------
  const mountedRef = useRef(true)
  const orgIdRef = useRef<string | null>(null)
  /** The in-flight token request; the double-mount guard. */
  const startRef = useRef<Promise<void> | null>(null)
  // Latest-prop mirrors, so the one-shot mount effect never closes over a
  // stale wizard snapshot (the profile changes on every keystroke).
  const resolveOrgIdRef = useRef(resolveOrgId)
  resolveOrgIdRef.current = resolveOrgId
  const onTokenIssuedRef = useRef(onTokenIssued)
  onTokenIssuedRef.current = onTokenIssued

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const start = useCallback(() => {
    if (startRef.current) return startRef.current

    const run: Promise<void> = (async () => {
      const orgId = await resolveOrgIdRef.current()
      orgIdRef.current = orgId
      return kybService.getAccessToken(orgId)
    })().then(
      result => {
        if (!mountedRef.current) return
        setToken(result.token)
        setPhase('ready')
        setError(null)
        onTokenIssuedRef.current(result.kyb_status)
      },
      (err: unknown) => {
        // Only a FAILED start clears the guard, so a retry is possible — and
        // only while the guard still holds THIS run: a retry may already have
        // started a newer request whose guard must not be clobbered.
        if (startRef.current === run) startRef.current = null
        if (!mountedRef.current) return
        if (err instanceof KybLevelUnresolvedError) {
          setPhase('blocked')
          return
        }
        if (err instanceof KybForbiddenError) {
          // Deliberate 403 — a role message, never a retryable error.
          setPhase('forbidden')
          return
        }
        setPhase('error')
        setError(
          err instanceof Error ? err.message : 'Could not start business verification',
        )
      },
    )

    startRef.current = run
    return run
  }, [])

  useEffect(() => {
    void start()
  }, [start])

  const retry = useCallback(() => {
    // Clear the guard even when it holds a RESOLVED promise: after a failed
    // token refresh the original start already succeeded, and reusing its
    // result would hand the SDK the same expired token back.
    startRef.current = null
    setPhase('starting')
    setError(null)
    setToken(null)
    void start()
  }, [start])

  /**
   * Called by the SDK on its own schedule. Must resolve to a fresh token; on
   * failure the step tears the SDK down and shows an inline retry — never an
   * auth event (the service call carries `skipAuthLogout`).
   */
  const expirationHandler = useCallback(async (): Promise<string> => {
    const orgId = orgIdRef.current
    try {
      if (!orgId) throw new Error('The organization is not ready yet')
      const result = await kybService.getAccessToken(orgId)
      return result.token
    } catch (err) {
      if (mountedRef.current) {
        setToken(null)
        setPhase('error')
        setError(
          'Your verification session expired and we could not refresh it. Your progress is saved with our verification partner — retry to continue where you left off.',
        )
      }
      throw err instanceof Error ? err : new Error('Token refresh failed')
    }
  }, [])

  const handleSdkMessage = useCallback((type: string) => {
    if (type === 'idCheck.onApplicantSubmitted' || type === 'idCheck.onApplicantResubmitted') {
      setSdkSubmitted(true)
    }
  }, [])

  const handleSdkError = useCallback(
    (sdkError: { code: string; error: string; reason?: string }) => {
      if (sdkError.code === 'invalid-token') {
        // The documented surface for an expired/bad token — same recovery as a
        // failed expirationHandler.
        if (mountedRef.current) {
          setToken(null)
          setPhase('error')
          setError(
            'Your verification session expired. Retry to continue where you left off.',
          )
        }
        return
      }
      // Non-fatal for the step: the applicant already exists, so surface the
      // message and leave the iframe (and the finish-later path) available.
      setSdkNotice(sdkError.error || sdkError.reason || 'The verification widget reported an error.')
    },
    [],
  )

  const handleMintLink = useCallback(async () => {
    const orgId = orgIdRef.current
    if (!orgId) return
    setMintingLink(true)
    try {
      const result = await kybService.createLink(orgId)
      if (mountedRef.current) setLink(result)
    } catch (err) {
      if (!mountedRef.current) return
      if (err instanceof KybLevelUnresolvedError) {
        setPhase('blocked')
        return
      }
      setSdkNotice(
        err instanceof Error ? err.message : 'Could not create a verification link',
      )
    } finally {
      if (mountedRef.current) setMintingLink(false)
    }
  }, [])

  // --- render --------------------------------------------------------------

  if (phase === 'starting') {
    return (
      <div className="flex flex-col items-center gap-3 py-16">
        <Loader2 className="h-6 w-6 animate-spin text-primary-500" />
        <p className={BODY_TEXT}>Preparing business verification…</p>
      </div>
    )
  }

  if (phase === 'blocked') {
    return (
      <div className="py-8">
        <AlertTriangle size={32} className="mb-4 text-[#ff540c]" />
        <SectionHeading
          title="Select your organization's legal form first"
          description="We use the legal form — LLC, corporation, trust, and so on — to run the right verification checks. It is on the first step of this wizard."
        />
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <Button type="button" variant="primary" onClick={onEditProfile} disabled={disabled}>
            Go to the profile step
          </Button>
          <Button type="button" variant="outline" onClick={retry} disabled={disabled}>
            I&apos;ve set it — try again
          </Button>
        </div>
      </div>
    )
  }

  if (phase === 'forbidden') {
    return (
      <div className="py-8">
        <AlertTriangle size={32} className="mb-4 text-[#ff540c]" />
        <SectionHeading
          title="An owner or admin must run verification"
          description="Only an organization owner or admin can complete business verification. Ask one of them to sign in and finish this step — your progress here is saved."
        />
      </div>
    )
  }

  if (phase === 'error') {
    return (
      <div className="py-8">
        <AlertTriangle size={32} className="mb-4 text-[#ff540c]" />
        <SectionHeading title="Verification could not start" />
        {error && <p className={cn(ERROR_TEXT, 'mb-4')}>{error}</p>}
        <Button type="button" variant="primary" onClick={retry} disabled={disabled}>
          Retry
        </Button>
      </div>
    )
  }

  return (
    <div>
      <SectionHeading
        title="Verify your business"
        description="Our verification partner Sumsub collects the company details, its registration documents, and its beneficial owners, in three steps. Everything you enter goes directly to Sumsub — it does not pass through SecondLane."
      />

      {token && (
        <SumsubWebSdk
          accessToken={token}
          expirationHandler={expirationHandler}
          config={{ lang: 'en' }}
          onMessage={handleSdkMessage}
          onError={handleSdkError}
        />
      )}

      {/* Below the widget, not above it. Above, this banner appeared at the
          moment the user submitted inside the iframe and pushed the whole
          several-hundred-pixel iframe down the page. Below, it also sits next
          to the Continue button in the footer that it tells them to use. */}
      {sdkSubmitted && (
        <p className={cn(BODY_TEXT, 'mt-4 flex items-start gap-2')}>
          <CheckCircle2 size={18} className="mt-[1px] shrink-0 text-[#1fb845]" />
          Verification submitted for review — you can continue to the final step.
        </p>
      )}

      {sdkNotice && <p className={cn(ERROR_TEXT, 'mt-4')}>{sdkNotice}</p>}

      <div className="mt-6 border-t border-border pt-4">
        {link ? (
          <p className={BODY_TEXT}>
            <a href={link.url} target="_blank" rel="noopener noreferrer" className="underline">
              Open your secure verification link
            </a>{' '}
            — it is single-use and expires in 3 days. You can submit now and finish verification
            from there.
          </p>
        ) : (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className={HELP_TEXT}>
              Need documents you don&apos;t have to hand? You can finish this later from a secure
              link — verification continues where you left off.
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={handleMintLink}
              isLoading={mintingLink}
              disabled={disabled}
            >
              Finish later — get a link
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
